/**
 * Subscription instances — several subscriptions of ONE vendor held by ONE
 * person (SPEC-00 §6 A3.9, founder answers A13/A18 2026-07-16).
 *
 * THE NAME A PERSON SEES. Founder, 2026-10-24: «чтобы была подпись, когда я
 * выбираю, под каким аккаунтом запускать агента … Например Claude 1, Claude 2».
 * A13 (2026-07-16) spelled the default "Subscription 1 / 2 / 3…"; the later and
 * more specific words win, so the default display name is the vendor's short
 * name plus the ordinal — "Claude 1", "Claude 2", "GPT 2" — and a person can
 * rename any instance.
 *
 * Migration 0276 backfilled the founder's two instance-1 rows with the literal
 * label 'Subscription 1' — a placeholder nobody typed. A stored label that is
 * exactly "Subscription <its own ordinal>" is therefore treated as no label,
 * so those rows read "Claude 1" / "GPT 1" like every other unnamed instance.
 *
 * ONE definition, imported by the gateway (what the API serves) and by the
 * interface (what a picker renders), so the two can never spell one instance
 * two ways.
 */
import { z } from 'zod';

/** Vendors whose subscriptions are spelled here. Mirrors the gateway's VENDORS. */
export const SUBSCRIPTION_INSTANCE_VENDORS = ['anthropic', 'openai', 'google'] as const;
export type SubscriptionInstanceVendor = (typeof SUBSCRIPTION_INSTANCE_VENDORS)[number];

/** The vendor word a person uses ("GPT", not "OpenAI" — founder 2026-10-04). */
export const SUBSCRIPTION_INSTANCE_VENDOR_NAME: Readonly<Record<SubscriptionInstanceVendor, string>> =
  Object.freeze({
    anthropic: 'Claude',
    openai: 'GPT',
    google: 'Gemini',
  });

/** Longest label a person may type. The column allows 120; a picker row does not. */
export const SUBSCRIPTION_INSTANCE_LABEL_MAX = 60;

/** The 0276 backfill placeholder for this ordinal — not a name anyone chose. */
function isPlaceholderLabel(label: string, ordinal: number): boolean {
  return label === `Subscription ${ordinal}`;
}

/**
 * "Claude 2", or the person's own label for that instance.
 *
 * Never throws: an unknown vendor reads as the vendor id itself, an unusable
 * ordinal as "?" — a display string must not be the thing that breaks a screen.
 */
export function subscriptionInstanceDisplayName(
  vendor: string,
  instanceOrdinal: number,
  label: string | null | undefined,
): string {
  const trimmed = typeof label === 'string' ? label.trim() : '';
  if (trimmed.length > 0 && !isPlaceholderLabel(trimmed, instanceOrdinal)) return trimmed;
  const name = (SUBSCRIPTION_INSTANCE_VENDOR_NAME as Record<string, string>)[vendor] ?? vendor;
  const n = Number.isSafeInteger(instanceOrdinal) && instanceOrdinal >= 1 ? String(instanceOrdinal) : '?';
  return `${name} ${n}`;
}

/** A label a person typed: trimmed, 1..MAX characters, one line. `null` clears it. */
export const subscriptionInstanceLabelSchema = z
  .string()
  .transform((value) => value.trim())
  .pipe(
    z
      .string()
      .min(1)
      .max(SUBSCRIPTION_INSTANCE_LABEL_MAX)
      .refine((value) => !/[\u0000-\u001f\u007f]/.test(value), 'one line, no control characters'),
  )
  .nullable();

/** Instance ordinal on the wire: a positive safe integer. */
export const subscriptionInstanceOrdinalSchema = z.number().int().min(1).max(1_000_000);

/** One row of a vendor's instance list, as the card picker receives it. */
export const cardSubscriptionInstanceSchema = z.object({
  instanceOrdinal: subscriptionInstanceOrdinalSchema,
  displayName: z.string(),
  status: z.enum(['disconnected', 'connecting', 'connected', 'expired', 'error']),
  disabled: z.boolean(),
  /** Could a spawn bind this instance right now (connected, enabled, subscription lane)? */
  runnable: z.boolean(),
});
export type CardSubscriptionInstance = z.infer<typeof cardSubscriptionInstanceSchema>;

/** One vendor's block of `GET /api/cards/:cardId/vendor-instances`. */
export const cardVendorInstanceChoiceSchema = z.object({
  vendor: z.enum(['anthropic', 'openai']),
  instances: z.array(cardSubscriptionInstanceSchema),
  /** The person's explicit pick for this card, or null = A18 default order. */
  chosenOrdinal: subscriptionInstanceOrdinalSchema.nullable(),
  /** The instance the NEXT run of this person on this card will mount, or null. */
  effectiveOrdinal: subscriptionInstanceOrdinalSchema.nullable(),
  /**
   * Why the effective instance is what it is:
   *   chosen   — the person's pick, and it is runnable;
   *   default  — no pick; A18 order (lowest runnable ordinal);
   *   fallback — the pick is not runnable right now (expired, disabled, not
   *              signed in), so A18 order applies until it is;
   *   none     — nothing of this vendor is runnable.
   */
  source: z.enum(['chosen', 'default', 'fallback', 'none']),
});
export type CardVendorInstanceChoice = z.infer<typeof cardVendorInstanceChoiceSchema>;

export const cardVendorInstanceChoicesResponseSchema = z.object({
  vendors: z.array(cardVendorInstanceChoiceSchema),
});
export type CardVendorInstanceChoicesResponse = z.infer<typeof cardVendorInstanceChoicesResponseSchema>;
